/**
 * The two things the Android shell has to tell the page, and the one it asks.
 *
 * Both directions exist because the platform gives the *Activity* facts the web
 * layer cannot see, and takes gestures the web layer is the only thing that can
 * answer. `src-tauri/gen/android/app/src/main/java/.../MainActivity.kt` is the
 * other half of this file.
 *
 * # Why this is not `env(safe-area-inset-*)`
 *
 * Because on Android that reports the **display cutout** and not the system
 * bars. It measured 52px at the top of a punch-hole phone in portrait -- which
 * looks exactly like a status bar being handled -- and 0px at the bottom with a
 * gesture pill plainly there, and in landscape the same 52px moved to the left
 * while the status bar stayed at the top and went back to overlapping. A device
 * with no cutout would report zero in every orientation and the app would sit
 * under the status bar everywhere.
 *
 * So Android's numbers come from `WindowInsets`, and the CSS falls back to
 * `env()` -- which is the right answer on iOS, where the notch and the home
 * indicator genuinely are reported.
 *
 * # Nothing here runs anywhere else
 *
 * Every export is a no-op without the bridge, so the browser and the desktop
 * build pay one `typeof` check at startup and are otherwise untouched. This is
 * deliberately not behind the transport seam: it is one platform inside one of
 * the two targets, and adding a third transport module to vary two functions
 * would be the divergence the parity guard exists to prevent.
 */

/** What `MainActivity` exposes. Absent everywhere but Android. */
interface AndroidBridge {
  /** `top,right,bottom,left` in CSS pixels. */
  insets(): string;
}

function bridge(): AndroidBridge | null {
  const found = (globalThis as { __hmAndroid?: AndroidBridge }).__hmAndroid;
  return found && typeof found.insets === "function" ? found : null;
}

/**
 * Whether this shell sizes the page from the viewport meta.
 *
 * Which is the question behind the UI scale setting, and it is not a question
 * about screens. A desktop browser ignores the tag outright, so the control has
 * to be **absent** there rather than present and inert -- the same rule the
 * offline pane and the double-click setting follow. Answered by the bridge for
 * now because Android is the only mobile shell this builds; iOS joins by
 * returning true here and nothing else changes.
 */
export function scalesViewport(): boolean {
  return bridge() !== null;
}

/** The custom properties the stylesheet reads, in the order the bridge sends. */
const SIDES = ["top", "right", "bottom", "left"] as const;

/**
 * Read the insets once at startup.
 *
 * The Activity also pushes them whenever they change -- a rotation, a keyboard,
 * switching between gesture and three-button navigation -- so this only has to
 * cover the case the push cannot: insets delivered before the document existed,
 * where the write lands on `about:blank` and is thrown away by the navigation
 * that follows.
 */
export function applyInsets(): void {
  const android = bridge();
  if (!android) return;
  const parts = android.insets().split(",");
  if (parts.length !== SIDES.length) return;
  SIDES.forEach((side, i) => {
    const value = Number(parts[i]);
    // A bridge that answered with something unparseable is a bridge to ignore,
    // rather than one to write `NaNpx` into the layout for.
    if (!Number.isFinite(value)) return;
    document.documentElement.style.setProperty(`--hm-inset-${side}`, `${value}px`);
  });
}

/**
 * Hand the system back gesture to the page.
 *
 * `onBack` returns whether it consumed the press; `MainActivity` leaves the app
 * on anything else, which is why the answer must be honest -- a back button
 * that silently does nothing is worse than one that closes the app.
 *
 * The decision itself is deliberately not here. What a back press means is a
 * question about layers, panes and window width, and `lib/navigation.ts` owns
 * all three; this file owns the wiring and the `typeof` check that keeps it
 * inert everywhere else.
 */
export function installBackHandler(onBack: () => boolean): void {
  if (!bridge()) return;
  (globalThis as { __hmBack?: () => boolean }).__hmBack = onBack;
}
